/**
 * Núcleo compartido de capas territoriales — catálogo, simbología y
 * helpers de conteo/GeoJSON. Usado por FichaTerritorialPanel (Ficha) y
 * LineOperationsPage (Cobertura): un solo lugar, no dos implementaciones.
 */
import type {
  AreaAccessibilityTier,
  CoverageEntitiesResult,
  MinimalAreaFeature,
  MinimalPointFeature,
  RouteMetricsResult,
} from '../api';

export type PointLayerKey =
  | 'trafficSignals'
  | 'trafficCalming'
  | 'crossings'
  | 'railwayCrossings'
  | 'streetlights'
  | 'education'
  | 'health'
  | 'civic'
  | 'secondary';
export type AreaLayerKey = 'greenSpace' | 'sportsFacility';
export type LayerKey = PointLayerKey | AreaLayerKey;

/**
 * Los dos universos que la interfaz debe distinguir con claridad (gate
 * "LineOperationsPage"): `route` se calcula sobre los edges realmente
 * recorridos por la variante, `coverage` se calcula por intersección con
 * la isócrona. `secondary` son entidades reales de interés urbano
 * (baños/fuentes/patrimonio/arte público/museos) que caen dentro de la
 * cobertura pero deliberadamente NUNCA entran al KPI principal.
 */
export type LayerGroup = 'route' | 'coverage' | 'secondary';

/** Qué pestaña posee cada capa — separado de `group` (que sigue siendo la etiqueta visual dentro del cajón de esa pestaña). Semáforos/reductores/cruces se movieron a Infraestructura vial a pedido del usuario; pasos ferroviarios/luminarias quedaron en Cobertura porque no se pidió moverlos. */
export type LayerTab = 'cobertura' | 'infraestructura';

export interface LayerConfig {
  key: LayerKey;
  label: string;
  color: string;
  kind: 'point' | 'area';
  group: LayerGroup;
  tab: LayerTab;
}

export const LAYERS: LayerConfig[] = [
  { key: 'trafficSignals', label: 'Semáforos', color: '#e5314d', kind: 'point', group: 'route', tab: 'infraestructura' },
  { key: 'trafficCalming', label: 'Reductores', color: '#fb8c00', kind: 'point', group: 'route', tab: 'infraestructura' },
  { key: 'crossings', label: 'Cruces peatonales', color: '#b7960a', kind: 'point', group: 'route', tab: 'infraestructura' },
  { key: 'railwayCrossings', label: 'Pasos ferroviarios', color: '#212121', kind: 'point', group: 'route', tab: 'cobertura' },
  { key: 'streetlights', label: 'Luminarias', color: '#f0b400', kind: 'point', group: 'route', tab: 'cobertura' },
  { key: 'education', label: 'Educación', color: '#1e88e5', kind: 'point', group: 'coverage', tab: 'cobertura' },
  { key: 'health', label: 'Salud', color: '#8e2450', kind: 'point', group: 'coverage', tab: 'cobertura' },
  { key: 'civic', label: 'Cívico / público', color: '#6a1b9a', kind: 'point', group: 'coverage', tab: 'cobertura' },
  { key: 'greenSpace', label: 'Espacios verdes', color: '#2e7d32', kind: 'area', group: 'coverage', tab: 'cobertura' },
  { key: 'sportsFacility', label: 'Deporte', color: '#00897b', kind: 'area', group: 'coverage', tab: 'cobertura' },
  { key: 'secondary', label: 'Otros equipamientos / interés urbano', color: '#795548', kind: 'point', group: 'secondary', tab: 'cobertura' },
];

export const LAYER_GROUP_LABELS: Record<LayerGroup, string> = {
  route: 'Sobre el recorrido',
  coverage: 'Dentro de la cobertura',
  secondary: 'Otros equipamientos / interés urbano',
};

export const SECONDARY_TYPE_LABELS: Record<string, string> = {
  toilets: 'Baños públicos',
  fountain: 'Fuentes',
  heritage_monument: 'Patrimonio / monumento',
  public_artwork: 'Arte público',
  museum: 'Museo',
};

export const PAVEMENT_LABELS: Record<string, string> = {
  ASPHALT: 'Asfalto',
  OTHER_PAVED: 'Otros pavimentos',
  UNPAVED: 'No pavimentado',
  UNKNOWN: 'Sin dato',
};

export const TIER_LABELS: Record<AreaAccessibilityTier, string> = {
  ACCESSIBLE_VIA_ACCESS_POINT: 'Acceso identificado dentro de la cobertura',
  ACCESSIBLE_VIA_PERIMETER: 'Perímetro accesible dentro de la cobertura',
  ACCESSIBLE_PERIMETER_OPEN: 'Perímetro abierto dentro de la cobertura',
  GEOMETRICALLY_NEAR: 'Cercano — acceso real no confirmado',
};

export interface SelectedFeature {
  kind: 'point' | 'area';
  category: string;
  name: string | null;
  osmSubtype: string | null;
  hasNetworkAssociation?: boolean;
  tier?: AreaAccessibilityTier;
}

/** Switch exhaustivo para que TS narrowee de verdad qué respuesta alimenta cada layer (un simple `===` inline no narrowea la key de un objeto indexado). */
export function pointFeaturesFor(
  key: PointLayerKey,
  routeMetrics: RouteMetricsResult | null,
  coverageEntities: CoverageEntitiesResult | null
): { count: number | undefined; features: MinimalPointFeature[] } {
  switch (key) {
    case 'education':
    case 'health':
    case 'civic':
      return { count: coverageEntities?.[key].count, features: coverageEntities?.[key].features ?? [] };
    case 'secondary':
      return { count: coverageEntities?.secondary.count, features: coverageEntities?.secondary.features ?? [] };
    case 'trafficSignals':
    case 'trafficCalming':
    case 'crossings':
    case 'railwayCrossings':
    case 'streetlights':
      return { count: routeMetrics?.[key].count, features: routeMetrics?.[key].features ?? [] };
  }
}

/** Conteo total mostrado junto al nombre de la capa en el panel — sin cálculos extra costosos, deriva de lo ya cargado. */
export function layerCount(
  layer: LayerConfig,
  routeMetrics: RouteMetricsResult | null,
  coverageEntities: CoverageEntitiesResult | null
): number | undefined {
  if (layer.kind === 'point') return pointFeaturesFor(layer.key as PointLayerKey, routeMetrics, coverageEntities).count;
  const area = coverageEntities?.[layer.key as AreaLayerKey];
  return area ? area.accessibleCount + area.nearCount : undefined;
}

export function pointsToGeoJSON(features: MinimalPointFeature[], category: string): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: features.map(f => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [f.lon, f.lat] },
      properties: {
        category,
        name: f.name,
        osmSubtype: f.osmSubtype,
        hasNetworkAssociation: f.hasNetworkAssociation,
        entityType: (f as { entityType?: string }).entityType ?? null,
      },
    })),
  };
}

export function areasToGeoJSON(features: MinimalAreaFeature[], category: string): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: features.map(f => ({
      type: 'Feature',
      geometry: f.geometry,
      properties: { category, name: f.name, osmSubtype: f.osmSubtype, tier: f.tier },
    })),
  };
}
